import React, { useEffect, useRef } from 'react';
import { Label } from 'semantic-ui-react';

// Sponsor leaderboard (728x90) in the lab's thumbnails row.
//
// The server decides who sees it (SponsorAds.show? -> lab_props.sponsor_ad);
// this component only renders what it is given. It counts one viewable
// impression per page view (>= 50% visible for >= 1s, via navigator.sendBeacon)
// and links through the server's click redirect, which counts the click and
// sends the visitor on to the sponsor even if this script fails.

const VIEWABLE_RATIO = 0.5;
const VIEWABLE_MS = 1000;

const makePageViewId = () => {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID().replace(/-/g, '');
    }
  } catch (e) {
    // fall through
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

const withParam = (url, key, value) => {
  const separator = url.indexOf('?') === -1 ? '?' : '&';
  return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

const sendImpression = (url, campaign, pv) => {
  const body = new URLSearchParams({ campaign, pv });
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;
  } catch (e) {
    // fall back to fetch below
  }
  try {
    fetch(url, { method: 'POST', body, keepalive: true, credentials: 'same-origin' }).catch(() => { });
  } catch (e) {
    // tracking must never break the lab
  }
};

// GA4 recommended promotion events (view_promotion / select_promotion), so the
// banner shows up in GA's promotion reports. They only fire from this
// component, i.e. in a real browser the server chose to show the ad to (bots and
// paying users never get it). Our own counts stay the source of
// truth for the sponsor; GA runs lower because ad blockers block it.
const sendAnalyticsEvent = (eventName, sponsor_ad) => {
  try {
    if (typeof gtag !== 'function') return;
    gtag('event', eventName, {
      promotion_id: sponsor_ad.campaign,
      promotion_name: sponsor_ad.sponsor || sponsor_ad.campaign,
      creative_name: sponsor_ad.creative_name,
      creative_slot: sponsor_ad.placement,
      transport_type: 'beacon'
    });
  } catch (e) {
    // analytics must never break the lab
  }
};

const SponsorBanner = ({ sponsor_ad, l, onLayoutChange }) => {
  const boxRef = useRef(null);
  const pvRef = useRef(null);
  if (pvRef.current === null) pvRef.current = makePageViewId();

  // Recompute the board height once the reserved box is in the layout, and
  // whenever the box's height changes (it scales with the window width).
  useEffect(() => {
    if (!sponsor_ad) return undefined;
    if (onLayoutChange) onLayoutChange();
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined' || !onLayoutChange) return undefined;
    let lastHeight = el.getBoundingClientRect().height;
    const observer = new ResizeObserver(() => {
      const height = el.getBoundingClientRect().height;
      if (Math.abs(height - lastHeight) < 0.5) return;
      lastHeight = height;
      onLayoutChange();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [sponsor_ad && sponsor_ad.campaign]);

  // One viewable impression per page view.
  useEffect(() => {
    if (!sponsor_ad) return undefined;
    const el = boxRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;

    let sent = false;
    let timer = null;
    let inView = false;

    const clearTimer = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const startTimer = () => {
      if (sent || timer || !inView || document.hidden) return;
      timer = setTimeout(() => {
        timer = null;
        if (sent || !inView || document.hidden) return;
        sent = true;
        sendImpression(sponsor_ad.impression_url, sponsor_ad.campaign, pvRef.current);
        sendAnalyticsEvent('view_promotion', sponsor_ad);
        observer.disconnect();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }, VIEWABLE_MS);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) clearTimer();
      else startTimer();
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        inView = entry.isIntersecting && entry.intersectionRatio >= VIEWABLE_RATIO;
        if (inView) startTimer();
        else clearTimer();
      });
    }, { threshold: [0, VIEWABLE_RATIO, 1] });

    observer.observe(el);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearTimer();
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sponsor_ad && sponsor_ad.campaign]);

  if (!sponsor_ad) return null;

  const width = sponsor_ad.width || 728;
  const height = sponsor_ad.height || 90;
  const sponsoredText = (l && l.sponsored) || 'Sponsored';
  const href = withParam(sponsor_ad.click_url, 'pv', pvRef.current);
  // The link itself navigates (and the server counts the click) even if this fails.
  const handleClick = () => sendAnalyticsEvent('select_promotion', sponsor_ad);

  return (
    <div
      id="sponsor-banner"
      className="sponsor-banner"
      ref={boxRef}
      data-placement={sponsor_ad.placement}
      data-campaign={sponsor_ad.campaign}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      <a
        href={href}
        target="_blank"
        rel="sponsored noopener"
        aria-label={`${sponsoredText}: ${sponsor_ad.alt}`}
        className="sponsor-banner__link"
        onClick={handleClick}
      >
        <img
          src={sponsor_ad.image_url}
          alt={sponsor_ad.alt}
          width={width}
          height={height}
          className="sponsor-banner__image"
        />
      </a>
      <Label size="mini" className="sponsor-banner__label" aria-hidden="true">
        {sponsoredText}
      </Label>
    </div>
  );
};

export default SponsorBanner;
